import {
  ColumnDef,
  createColumnHelper,
  FilterFnOption,
  Row,
  SortingFn,
  sortingFns,
} from '@tanstack/react-table';
import { clsx } from 'clsx';
import { SeatingInfo } from 'src/components/common/SeatingInfo';
import { AutoPriceResultDisplay } from 'src/components/Listings/AutoPriceResultDisplay';
import { AutoPriceResultStateBadge } from 'src/components/Listings/AutoPriceResultStateBadge';
import { FAKE_SOLD_GROUP_ID } from 'src/components/Listings/constants';
import { ListingDeliveryTypeDisplay } from 'src/components/Listings/ListingDeliveryTypeDisplay';
import { ListingDisplayV2 } from 'src/components/Listings/ListingDisplay/ListingDisplayV2';
import { ListingNotesDisplay } from 'src/components/Listings/ListingDisplay/ListingNotesDisplay';
import { TextCell } from 'src/components/TableV2/cells';
import { BaseCell } from 'src/components/TableV2/cells/BaseCell';
import { ColumnPicker } from 'src/components/TableV2/components/ColumnPicker';
import { TagValueDisplay } from 'src/components/TagsFormBody/TagValueCell';
import { useAppContext } from 'src/contexts/AppContext';
import {
  COLUMN_DEFAULT_SIZE_BASE,
  COLUMN_DEFAULT_SIZE_ICON,
  COLUMN_DEFAULT_SIZE_LG,
  COLUMN_DEFAULT_SIZE_SM,
  COLUMN_DEFAULT_SIZE_XL,
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Stack, Tooltip } from 'src/core/ui';
import { useConsigneeAccountDisplayName } from 'src/hooks/api/useConsigneeAccountDisplayName';
import { useColumnUserSetting } from 'src/hooks/useColumnUserSetting';
import { useMapDeliveryTypesToString } from 'src/hooks/useMapDeliveryTypesToString';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { FormatOption, FormatOptionEntries } from 'src/modals/EditTableColumns';
import { CustomColumnName } from 'src/modals/InventoryTableColumns/CustomColumnName';
import { MarketplaceBroadcastCell } from 'src/modals/ListingDetails/ListingForms/BroadcastForm/MarketplaceBroadcastSection';
import { AnchorIcon } from 'src/svgs/AnchorIcon';
import { ListingGroupIcon } from 'src/svgs/ListingGroupIcon';
import { ExpandIcon, ForwardIcon } from 'src/svgs/Viagogo';
import { ChevronDownIconV2 } from 'src/svgs/Viagogo/goicons/ChevronDownIconV2';
import { ChevronUpIconV2 } from 'src/svgs/Viagogo/goicons/ChevronUpIconV2';
import { SEATING_SORTING_FN_NAME } from 'src/tables/common/hooks/useSeatingColumnSorting';
import { TableShimmeringDiv } from 'src/tables/Table/Table.styled';
import { InHandCell } from 'src/tables/Table/TableCells';
import { createCopyColumn } from 'src/tables/TableV2/factories/copyColumnFactory';
import { createUserTagColumn } from 'src/tables/TableV2/factories/userTagColumnFactory';
import { useContextBasedUsers } from 'src/tables/TableV2/factories/userTagColumnHooks';
import {
  booleanFilter,
  compListingMinQuantityFilter,
  dateRangeFilter,
  marketplaceBroadcastFilter,
  moneyRangeFilter,
  multiSelectFilter,
  numberRangeFilter,
  outlierSpacingFilter,
  outlierStdDevFilter,
  percentRangeFilter,
  seatRangeFilter,
  seatTraitsFilter,
  textSearchFilter,
} from 'src/tables/TableV2/filters/filterFunctions';
import {
  getCompListingModeIcon,
  getUndercutModeIcon,
} from 'src/utils/autoPricingUtils';
import { ListingTableColumnId } from 'src/utils/columns/inventory/inventoryColumnUtils.types';
import { evaluateFormula } from 'src/utils/columns/inventory/inventoryCustomColumnUtils';
import { CustomListingColumn } from 'src/utils/columns/inventory/inventoryCustomColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID } from 'src/utils/constants/contentIdMaps';
import {
  getAllInPriceFromListPrice,
  getAllInPriceFromListPriceForListing,
  getListingDetailsRelativeUrl,
  isListingGroupLeader,
} from 'src/utils/inventoryUtils';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import { getPurchaseOrderRelativeUrl } from 'src/utils/purchaseUtils';
import { hashStringToNegativeNumber } from 'src/utils/stringUtils';
import {
  filterSeatingSectionFn,
  getNextEditableListingRow,
  sortGeneric,
  sortMoney,
  sortSeatingSeatsForTable,
} from 'src/utils/tableUtils';
import {
  SECTION_TYPE_TO_PRECISION_SETTING,
  SectionType,
} from 'src/utils/types/sectionType';
import {
  AutoPricingCompListingMode,
  AutoPricingConstraintType,
  AutoPricingResultType,
  AutoPricingUndercutMode,
  Feature,
  ListingDetailDataField,
  ListingGroup,
  ListingNote,
  ListingNoteType,
  Marketplace,
  TagsValueType,
  UiMoney,
  UserSetting,
} from 'src/WebApiController';

import { createDateColumn } from '../../TableV2/factories/dateColumnFactory';
import { createLinkColumn } from '../../TableV2/factories/linkColumnFactory';
import { createMoneyColumn } from '../../TableV2/factories/moneyColumnFactory';
import { createNumberColumn } from '../../TableV2/factories/numberColumnFactory';
import { createPercentColumn } from '../../TableV2/factories/percentColumnFactory';
import * as styles from '../ListingTable.css';
import {
  ListingWithEvent,
  SHIMMERING_DIV_HEIGHT_LISTING_GROUP,
  SHIMMERING_DIV_HEIGHT_SINGLE_LISTING,
} from '../ListingTable.types';
import { AutoPricingToggleCell } from './AutoPricingToggleCell';
import { CompListingBoundsCell } from './CompListingBoundsCell';
import { CustomNumericCell } from './CustomNumericCell';
import { ListingActionsCellV2 } from './ListingActionsCellV2';
import { ListingPriceCell } from './ListingPriceCell';
import { ListingProceedsFloorCell } from './ListingProceedsFloorCell';
import { PricingModeCell } from './PricingModeCell';
import { CircuitBreakerMinCompListingCountCell } from './PricingSettings/CircuitBreakerMinCompListingCount';
import { CircuitBreakerVelocityCell } from './PricingSettings/CircuitBreakerVelocity';
import { CircuitBreakerVelocityHoursCell } from './PricingSettings/CircuitBreakerVelocityPeriod';
import { CompListingExcludeFanInventoryCell } from './PricingSettings/CompListingExcludeFanInventory';
import { CompListingMinQuantityCell } from './PricingSettings/CompListingMinQuantity';
import { OutlierModeCell } from './PricingSettings/OutlierDetectionMode';
import { OutlierFilteringCell } from './PricingSettings/OutlierFiltering';
import { OutlierLimitCell } from './PricingSettings/OutlierLimit';
import { OutlierSpacingCell } from './PricingSettings/OutlierSpacing';
import { UndercutAmountCell } from './UndercutAmountCell';
import { useShouldHideInvisibleGroupData } from './useShouldHideInvisibleGroupData';

const SECTION_TYPE = SectionType.Listings;

const GroupListingsSortableFields: ListingTableColumnId[] = [
  ListingTableColumnId.ListPrice,
  ListingTableColumnId.AllInPrice,
  ListingTableColumnId.ProceedsCeiling,
  ListingTableColumnId.ProceedsFloor,
  ListingTableColumnId.WebsiteCeiling,
  ListingTableColumnId.WebsiteFloor,
  ListingTableColumnId.UnsoldQuantity,
  ListingTableColumnId.AvailableQuantity,
  ListingTableColumnId.ShownQuantity,
];

const findParentRow = (element: HTMLElement | null): HTMLElement | null => {
  if (!element) {
    return null;
  }
  if (element.tagName.toLowerCase() === 'tr') {
    return element;
  }
  return findParentRow(element.parentElement);
};

/**
 * Sorting wrapper to make sure sorting will not be applied to grouped items.
 * @param desiredSortingFn
 * @returns shadowed sorting fn
 */
const groupSortingWrapper = (
  desiredSortingFn: SortingFn<ListingWithEvent | null>,
  columnId: ListingTableColumnId | undefined = undefined,
  hasGroupListingsSortingFeature = false,
  canSortAnySubrows = false
): SortingFn<ListingWithEvent | null> => {
  return (
    rowA: Row<ListingWithEvent | null>,
    rowB: Row<ListingWithEvent | null>,
    colId: string
  ) => {
    const canSort =
      hasGroupListingsSortingFeature &&
      columnId &&
      GroupListingsSortableFields.includes(columnId);
    if (rowA.depth + rowB.depth > 0 && !canSort && !canSortAnySubrows) return 0;
    return desiredSortingFn(rowA, rowB, colId);
  };
};

export const tagColumnDef = (
  tagName: string,
  columnId: ListingTableColumnId | undefined = undefined,
  canSortGroup = false,
  canSortAnySubrows = false
): ColumnDef<ListingWithEvent | null> => ({
  id: tagName,
  header: () => (
    <span style={{ overflowX: 'clip', textOverflow: 'ellipsis' }}>
      {tagName}
    </span>
  ),
  enableColumnFilter: false,
  size: COLUMN_DEFAULT_SIZE_LG,
  minSize: COLUMN_MIN_SIZE,
  maxSize: COLUMN_MAX_SIZE,
  accessorFn: (data) => {
    if (data?.isCompListing) return undefined;

    const tag = data?.listing?.tags?.find((t) => t.key === tagName);
    if (
      tag?.valueType === TagsValueType.Int ||
      tag?.valueType === TagsValueType.Decimal
    ) {
      return tag ? parseFloat(tag.value) : null;
    }
    return tag?.value;
  },
  cell: function Cell({ getValue, row: { original }, table }) {
    const { value: storedColumnNumberPrecisions = {} } =
      useServerUserSetting<FormatOptionEntries>({
        id: SECTION_TYPE_TO_PRECISION_SETTING[
          table.options.meta?.sectionType ?? SECTION_TYPE
        ],
      });
    const tag = original?.listing?.tags?.find((t) => t.key === tagName);

    if (
      tag?.valueType === TagsValueType.Int ||
      tag?.valueType === TagsValueType.Decimal
    ) {
      const userDefinedPrecision = storedColumnNumberPrecisions[tagName];

      return (
        <CustomNumericCell
          columnId={tagName}
          userDefinedPrecision={userDefinedPrecision}
          value={getValue<number | null>()}
          isLoading={Boolean(!original || !original.listing)}
          // We do not display custom fields for listing group or comp listing
          hide={Boolean(
            original?.listing?.isLtGrp || !original?.listing?.isFull
          )}
          shimmeringDivHeight={
            original?.isLtGrp
              ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
              : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
          }
          sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
          matchingTag={tag}
          currencyCode={original?.listing?.currency}
        />
      );
    }

    if (
      !original ||
      !original.listing ||
      (!original?.listing?.isLtGrp &&
        !original?.isCompListing &&
        !original.listing?.dataIsLoaded?.[ListingDetailDataField.Tags])
    ) {
      return (
        <TableShimmeringDiv
          height={
            original?.isLtGrp
              ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
              : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
          }
        />
      );
    }

    if (original?.listing?.isLtGrp || !original?.listing?.isFull || !tag) {
      return null;
    }

    return (
      <BaseCell align="right">
        <TagValueDisplay tag={tag} displayMode />
      </BaseCell>
    );
  },
  sortingFn: groupSortingWrapper(
    sortingFns.basic,
    columnId,
    canSortGroup,
    canSortAnySubrows
  ),
});

export const customColumnDef = (
  columnId: string,
  formula?: string | null,
  canSortGroup = false,
  canSortAnySubrows = false
): ColumnDef<ListingWithEvent | null> => ({
  id: columnId,
  header: ({ table }) => (
    <CustomColumnName
      id={columnId}
      sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
    />
  ),
  enableColumnFilter: false,
  size: COLUMN_DEFAULT_SIZE_LG,
  minSize: COLUMN_MIN_SIZE,
  maxSize: COLUMN_MAX_SIZE,
  accessorFn: (data) => {
    return formula && data && !data.isCompListing
      ? (evaluateFormula(formula, data.listing) ?? null)
      : null;
  },
  cell: function Cell({ getValue, row: { original }, table }) {
    const { value: storedInventoryColumnNumberPrecisions = {} } =
      useServerUserSetting<FormatOptionEntries>({
        id: SECTION_TYPE_TO_PRECISION_SETTING[
          table.options.meta?.sectionType ?? SECTION_TYPE
        ],
      });
    const { value: customColumns = [] } = useServerUserSetting<
      CustomListingColumn[]
    >({
      id: UserSetting.InventoryCustomColumns,
    });

    const userDefinedPrecision =
      storedInventoryColumnNumberPrecisions[columnId];

    return (
      <CustomNumericCell
        columnId={columnId}
        userDefinedPrecision={userDefinedPrecision}
        value={getValue<number | null>()}
        isLoading={Boolean(!original || !original.listing)}
        // We do not display custom fields for listing group or comp listing
        hide={Boolean(original?.listing?.isLtGrp || !original?.listing?.isFull)}
        shimmeringDivHeight={
          original?.isLtGrp
            ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
            : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
        }
        sectionType={table.options.meta?.sectionType ?? SECTION_TYPE}
        matchingCustomColumn={customColumns.find((c) => c.id === columnId)}
        currencyCode={original?.listing?.currency}
      />
    );
  },
  sortingFn: groupSortingWrapper(
    sortingFns.basic,
    columnId as ListingTableColumnId,
    canSortGroup,
    canSortAnySubrows
  ),
});

const columnHelper = createColumnHelper<ListingWithEvent | null>();

export const getListingTableColumnConfigsV2 = (
  canSortGroup = false,
  hasGroup = false,
  canSortAnySubrows = false,
  editableColumns: Set<string> = new Set(),
  inFullPageEventView = false
) => {
  return {
    [ListingTableColumnId.TicketClassColor]: columnHelper.accessor(
      (data: ListingWithEvent | null) => data?.ticketClassColor ?? '',
      {
        id: ListingTableColumnId.TicketClassColor,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.TicketClassColor
              ]
            }
          />
        ),
        enableColumnFilter: false,
        size: COLUMN_DEFAULT_SIZE_BASE,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,

        cell: function Cell({ getValue, row: { original } }) {
          if (!original || !original.listing) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }

          const ticketClassColor = getValue<string | null>();

          return (
            <BaseCell align="center">
              <div
                style={{
                  height: vars.iconSize.m,
                  width: vars.iconSize.m,
                  borderRadius: '50%',
                  backgroundColor: ticketClassColor ?? '',
                }}
              ></div>
            </BaseCell>
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.TicketClassColor,
          canSortGroup,
          canSortAnySubrows
        ),
      }
    ),
    [ListingTableColumnId.ListingId]: createLinkColumn<ListingWithEvent | null>(
      {
        id: ListingTableColumnId.ListingId,
        headerContentId:
          LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[ListingTableColumnId.ListingId],
        linkAccessor: (data) =>
          data?.listing?.id
            ? getListingDetailsRelativeUrl(data.listing.id)
            : null,
        textAccessor: (data) =>
          data?.listing?.id ? String(data.listing.id) : null,
        size: COLUMN_DEFAULT_SIZE_LG,
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.ListingId,
          canSortGroup,
          canSortAnySubrows
        ),
        shouldRenderCell: (data) =>
          !!(
            data?.listing &&
            !data.listing.isLtGrp &&
            data.listing.isFull &&
            !data.isCompListing
          ),
      }
    ),
    [ListingTableColumnId.Seating]: columnHelper.accessor(
      (data) =>
        data?.listing
          ? {
              seating: data.listing.seating,
              listingNotes: data.listing.seatTraits,
            }
          : null,
      {
        id: ListingTableColumnId.Seating,
        header: function Header({ table }) {
          const seatingText = useContent(
            LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[ListingTableColumnId.Seating]
          );
          const sectionText = useContent(ContentId.Section);
          const { value: storedInventoryColumnNumberPrecisions = {} } =
            useServerUserSetting<FormatOptionEntries>({
              id: SECTION_TYPE_TO_PRECISION_SETTING[
                table.options.meta?.sectionType ?? SECTION_TYPE
              ],
            });

          const isSectionOnly =
            storedInventoryColumnNumberPrecisions[
              ListingTableColumnId.Seating
            ] === FormatOption.Seating_SectionOnly;

          return (
            <Stack direction="row" style={{ paddingLeft: '0px' }}>
              <span>
                {isSectionOnly
                  ? `${seatingText} (${sectionText})`
                  : seatingText}
              </span>
            </Stack>
          );
        },
        enableColumnFilter: true,
        filterFn:
          filterSeatingSectionFn as FilterFnOption<ListingWithEvent | null>,
        meta: {
          columnFilterVariant: 'multiSelect',
          uniqueValueVariant: 'seatingSection',
        },
        size: COLUMN_DEFAULT_SIZE_XL,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({
          row: {
            original,
            getCanExpand,
            getIsExpanded,
            toggleExpanded,
            depth,
            parentId,
            getParentRow,
          },
          cell: { id: cellId },
          column: { id: columnId },
          table,
        }) {
          const { isDarkMode } = useSiteTheme();

          const { userDefinedPrecision } = useColumnUserSetting(
            columnId,
            table.options.meta?.sectionType ?? SECTION_TYPE
          );

          if (!original || !original.listing) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }

          const adjustedListing =
            userDefinedPrecision === FormatOption.Seating_SectionOnly
              ? {
                  ...original.listing,
                  seating: {
                    ...original.listing.seating,
                    row: null,
                    seatFr: null,
                    seatTo: null,
                  },
                }
              : original.listing;

          const canExpand = getCanExpand();
          const isExpanded = getIsExpanded();
          const Icon = isExpanded ? ExpandIcon : ForwardIcon;

          return (
            <BaseCell>
              <Stack direction="row" gap="m" alignItems="center" width="full">
                <Stack
                  direction="row"
                  justifyContent="spaceBetween"
                  width="full"
                  alignItems="center"
                >
                  <Stack
                    title={original?.selectionDisabledReason}
                    direction="column"
                    gap="s"
                    width="full"
                  >
                    <ListingDisplayV2
                      listing={adjustedListing!}
                      showOrgSeat={
                        userDefinedPrecision ===
                        FormatOption.Seating_OriginalSeating
                      }
                      width="full"
                      justifySeatingIconContent="spaceBetween"
                      allowDisplaySeatingOverride={true}
                    />
                  </Stack>
                  <div style={{ flexShrink: 0, display: 'flex' }}>
                    {original.ltGroupName && (
                      <Tooltip
                        contentVariant={isDarkMode ? 'light' : 'darkGray'}
                        triggerContent={
                          <Stack
                            justifyContent="center"
                            alignItems="center"
                            style={{
                              backgroundColor: vars.color.surfaceHighlight,
                              borderRadius: vars.borderRadius.s,
                              width: vars.iconSize.xl,
                              height: vars.iconSize.xl,
                              alignItems: 'center',
                            }}
                          >
                            <AnchorIcon size={vars.iconSize.s} />
                          </Stack>
                        }
                        align="center"
                      >
                        <div>
                          <Content id={ContentId.MergeListingGroupTooltip} />{' '}
                          <strong>{original.ltGroupName}</strong>
                        </div>
                      </Tooltip>
                    )}
                  </div>
                </Stack>
              </Stack>
            </BaseCell>
          );
        },
        sortingFn: SEATING_SORTING_FN_NAME,
      }
    ),
    [ListingTableColumnId.SeatTraits]: columnHelper.accessor(
      (data) => {
        const seatTraits: ListingNote[] = data?.listing?.seatTraits ?? [];

        const adminHoldNote = data?.listing?.adminHoldNotes;

        if (adminHoldNote) {
          const adminNote: ListingNote = {
            listingNoteId: hashStringToNegativeNumber(adminHoldNote), // Not a real noteId
            listingNoteText: adminHoldNote, // Use the admin hold note for the text.
            listingNoteUntranslated: adminHoldNote, // Same text for untranslated.
            sellerAccountId: null, // Set to null
            stubhubListingNoteId: null, // Set to null.
            type: ListingNoteType.RestrictionOnUse,
          };

          return [adminNote, ...seatTraits];
        }

        return seatTraits;
      },
      {
        id: ListingTableColumnId.SeatTraits,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.SeatTraits
              ]
            }
          />
        ),
        enableColumnFilter: true,
        filterFn: seatTraitsFilter as FilterFnOption<ListingWithEvent | null>,
        meta: {
          columnFilterVariant: 'multiSelect',
          uniqueValueVariant: 'seatTraits',
        },
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        enableSorting: false,
        cell: ({ row: { original }, getValue }) => {
          const seatTraits = getValue<ListingNote[] | null | undefined>();

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;
          // We do not display for listing group or comp listing
          if (original.listing.isLtGrp || original.isCompListing) return null;
          if (!seatTraits?.length) return null;

          return (
            <BaseCell align="left" showTooltip={false}>
              <ListingNotesDisplay seatTraits={seatTraits} />
            </BaseCell>
          );
        },
      }
    ),
    [ListingTableColumnId.DeliveryType]: columnHelper.accessor(
      (data) => data?.listing?.delivType,
      {
        id: ListingTableColumnId.DeliveryType,
        header: function Cell() {
          const titleText = useContent(
            LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
              ListingTableColumnId.DeliveryType
            ]
          );

          return <div>&nbsp;</div>;
        },
        enableColumnFilter: true,
        size: COLUMN_DEFAULT_SIZE_ICON,
        minSize: COLUMN_DEFAULT_SIZE_ICON,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({
          row: { original, getCanExpand },
          column: { id: columnId },
          table,
        }) {
          const { userDefinedPrecision } = useColumnUserSetting(
            columnId,
            table.options.meta?.sectionType ?? SECTION_TYPE
          );

          const strDeliveryTypes = useMapDeliveryTypesToString({
            deliveryTypesObj: original?.listing,
          });

          if (!original || !original.listing) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }

          // We do not display for comp listing
          if (original.isCompListing) return null;

          // Don't show delivery type when row can be expanded
          if (getCanExpand()) {
            return null;
          }

          return (
            <BaseCell
              align="left"
              title={strDeliveryTypes}
              style={{ height: '100%' }}
            >
              <div
                style={{
                  borderRadius: vars.borderRadius.m,
                  padding: `${vars.spacing['xxs']} ${vars.spacing['sm']}`,
                  backgroundColor: vars.color.surfaceSecondary,
                  width: '100%',
                }}
              >
                <ListingDeliveryTypeDisplay
                  listing={original.listing}
                  userDefinedPrecision={userDefinedPrecision}
                  showIconTitle={false}
                />
              </div>
            </BaseCell>
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.DeliveryType,
          canSortGroup,
          canSortAnySubrows
        ),
        filterFn: multiSelectFilter,
        meta: {
          columnFilterVariant: 'multiSelect',
        },
      }
    ),

    [ListingTableColumnId.ShownQuantity]: createNumberColumn<ListingWithEvent>({
      id: ListingTableColumnId.ShownQuantity,
      headerContentId:
        LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
          ListingTableColumnId.ShownQuantity
        ],
      accessor: (data) =>
        data?.listing?.manMaxDispQty ?? data?.listing?.maxDispQty ?? 0,
      size: COLUMN_DEFAULT_SIZE_SM,
      sectionType: SECTION_TYPE,
      sortingFn: groupSortingWrapper(
        sortingFns.basic,
        ListingTableColumnId.ShownQuantity,
        canSortGroup,
        canSortAnySubrows
      ),
      customCell: function ShownQuantityCell({
        getValue,
        row: { original },
        column: { id: columnId },
        table,
      }) {
        const { userDefinedPrecision } = useColumnUserSetting(
          columnId,
          table.options.meta?.sectionType ?? SECTION_TYPE
        );
        const shouldHideInvisibleGroupData =
          useShouldHideInvisibleGroupData(original);

        if (!original || !original.listing) {
          return (
            <TableShimmeringDiv
              height={
                original?.isLtGrp
                  ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                  : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
              }
            />
          );
        }

        // We do not display for comp listing
        if (original.isCompListing) return null;
        if (shouldHideInvisibleGroupData) return null;
        const maxDisplayQuantity = getValue() as number;
        const sanitizedDisplayQuantity = Math.min(
          maxDisplayQuantity,
          original.listing?.unsoldQty ?? 0
        );

        return (
          <BaseCell align="right">
            <div
              className={
                isListingGroupLeader(original.listing) ? styles.bold : ''
              }
            >
              {applyNumberFormatting({
                precision: userDefinedPrecision,
                inputNumber: sanitizedDisplayQuantity,
              })}
            </div>
          </BaseCell>
        );
      },
      withFooter: true,
    }),

    [ListingTableColumnId.SplitType]: columnHelper.accessor(
      (data) => data?.listing?.splitType,
      {
        id: ListingTableColumnId.SplitType,
        header: ({ column }) => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.SplitType
              ]
            }
          />
        ),
        enableColumnFilter: true,
        size: COLUMN_DEFAULT_SIZE_SM,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({
          getValue,
          row: { original },
          column: { id: columnId },
          table,
        }) {
          if (!original || !original.listing) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }

          // We do not display for comp listing
          if (original.isCompListing) return null;

          return (
            <BaseCell isFullWidth>
              <div
                className={
                  isListingGroupLeader(original.listing) ? styles.bold : ''
                }
                style={{
                  borderRadius: vars.borderRadius.m,
                  padding: `${vars.spacing['xxs']} ${vars.spacing['sm']}`,
                  backgroundColor: vars.color.surfaceSecondary,
                  width: '100%',
                }}
              >
                {getValue()}
              </div>
            </BaseCell>
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.SplitType,
          canSortGroup,
          canSortAnySubrows
        ),
        filterFn: multiSelectFilter,
        meta: {
          columnFilterVariant: 'multiSelect',
        },
      }
    ),
    [ListingTableColumnId.UnsoldQuantity]: createNumberColumn<ListingWithEvent>(
      {
        id: ListingTableColumnId.UnsoldQuantity,
        headerContentId:
          LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
            ListingTableColumnId.UnsoldQuantity
          ],
        accessor: (data) => data?.listing?.unsoldQty,
        size: COLUMN_DEFAULT_SIZE_SM,
        sectionType: SECTION_TYPE,
        withFooter: true,
        className: (data) =>
          isListingGroupLeader(data?.listing) ? styles.bold : '',
        shouldRender: (data) => !data?.shouldHideNumberColumn,
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.UnsoldQuantity,
          canSortGroup,
          canSortAnySubrows
        ),
      }
    ),
    [ListingTableColumnId.AvailableQuantity]:
      createNumberColumn<ListingWithEvent>({
        id: ListingTableColumnId.AvailableQuantity,
        headerContentId:
          LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
            ListingTableColumnId.AvailableQuantity
          ],
        accessor: (data) => data?.listing?.availQty,
        size: COLUMN_DEFAULT_SIZE_SM,
        sectionType: SECTION_TYPE,
        withFooter: true,
        className: (data) =>
          isListingGroupLeader(data?.listing) ? styles.bold : '',
        shouldRender: (data) => !data?.shouldHideNumberColumn,
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.AvailableQuantity,
          canSortGroup,
          canSortAnySubrows
        ),
      }),
    [ListingTableColumnId.TicketCount]: createNumberColumn<ListingWithEvent>({
      id: ListingTableColumnId.TicketCount,
      headerContentId:
        LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[ListingTableColumnId.TicketCount],
      accessor: (data) => data?.listing?.ticketCnt ?? 0,
      size: COLUMN_DEFAULT_SIZE_SM,
      sectionType: SECTION_TYPE,
      withFooter: true,
      className: (data) =>
        isListingGroupLeader(data?.listing) ? styles.bold : undefined,
      shouldRender: (data) =>
        !data?.isCompListing && !data?.shouldHideNumberColumn,
      sortingFn: groupSortingWrapper(
        sortingFns.basic,
        ListingTableColumnId.TicketCount,
        canSortGroup,
        canSortAnySubrows
      ),
    }),
    [ListingTableColumnId.SoldQty]: createNumberColumn<ListingWithEvent>({
      id: ListingTableColumnId.SoldQty,
      headerContentId:
        LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[ListingTableColumnId.SoldQty],
      accessor: (data) => data?.listing?.soldQty ?? 0,
      size: COLUMN_DEFAULT_SIZE_LG,
      sectionType: SECTION_TYPE,
      withFooter: true,
      className: (data) =>
        isListingGroupLeader(data?.listing) ? styles.bold : undefined,
      shouldRender: (data) =>
        !data?.isCompListing && !data?.shouldHideNumberColumn,
      sortingFn: groupSortingWrapper(
        sortingFns.basic,
        ListingTableColumnId.SoldQty,
        canSortGroup,
        canSortAnySubrows
      ),
    }),
    [ListingTableColumnId.InHandDate]: columnHelper.accessor(
      (data) => data?.listing?.inHandAt || '',
      {
        id: ListingTableColumnId.InHandDate,
        header: ({ column }) => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.InHandDate
              ]
            }
          />
        ),
        enableColumnFilter: true,
        size: COLUMN_DEFAULT_SIZE_ICON,
        minSize: COLUMN_DEFAULT_SIZE_ICON,
        maxSize: COLUMN_MAX_SIZE,
        cell: ({ getValue, row: { original } }) => {
          const value = getValue<string>();
          if (!original || !original.listing) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }

          // We do not display inhand for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return null;

          return (
            <BaseCell align="left">
              <InHandCell date={value ? new Date(value) : null} />
            </BaseCell>
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.datetime,
          ListingTableColumnId.InHandDate,
          canSortGroup,
          canSortAnySubrows
        ),
        filterFn: dateRangeFilter,
        meta: {
          columnFilterVariant: 'dateRange',
        },
      }
    ),
    [ListingTableColumnId.UnitCost]: createMoneyColumn<ListingWithEvent>({
      id: ListingTableColumnId.UnitCost,
      headerContentId:
        LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[ListingTableColumnId.UnitCost],
      accessor: (data) => data?.listing?.unitCst ?? data?.listing?.faceValue,
      size: COLUMN_DEFAULT_SIZE_BASE,
      useUserPrecision: true,
      withFooter: true,
      className: (data) =>
        isListingGroupLeader(data?.listing) ? styles.bold : undefined,
      shouldRender: (data) =>
        Boolean(
          data?.listing && !data?.isCompListing && !data?.shouldHideNumberColumn
        ),
      customPrecision: ({ value, userDefinedPrecision, isServerSetting }) => {
        // Hide decimals if >999 and this is a default precision
        const shouldRoundToWhole = !isServerSetting && value.amt > 999;
        return shouldRoundToWhole ? FormatOption.Whole : userDefinedPrecision;
      },
    }),

    [ListingTableColumnId.UnsoldProceeds]: createMoneyColumn<ListingWithEvent>({
      id: ListingTableColumnId.UnsoldProceeds,
      headerContentId:
        LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
          ListingTableColumnId.UnsoldProceeds
        ],
      accessor: (data) => {
        if (!data?.listing) return null;

        const amt = data.listing.listPrice * data.listing.availQty;

        if (amt == null) return null;

        return {
          amt,
          currency: data.listing.currency,
          dec: 2,
          disp: '', // Not used by MoneyCell when we have amt
        };
      },
      size: COLUMN_DEFAULT_SIZE_LG,
      useUserPrecision: true,
      withFooter: true,
      shouldRender: (data) =>
        Boolean(
          data?.listing &&
          !data?.listing?.isLtGrp &&
          !data?.isCompListing &&
          !data?.shouldHideNumberColumn
        ),
      customPrecision: ({ value, userDefinedPrecision, isServerSetting }) => {
        const shouldRoundToWhole = !isServerSetting && value.amt > 999;
        return shouldRoundToWhole ? FormatOption.Whole : userDefinedPrecision;
      },
      sortingFn: groupSortingWrapper(
        sortMoney,
        ListingTableColumnId.UnsoldProceeds,
        canSortGroup,
        canSortAnySubrows
      ),
    }),
    [ListingTableColumnId.UnsoldCost]: createMoneyColumn<ListingWithEvent>({
      id: ListingTableColumnId.UnsoldCost,
      headerContentId:
        LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[ListingTableColumnId.UnsoldCost],
      accessor: (data) => {
        if (!data?.listing) return null;

        // Return server-side aggregated value when possible
        if (data.listing.isLtGrp && data.listing.unsoldCost) {
          return data.listing.unsoldCost;
        }

        const amt =
          data.listing.unitCst || data.listing.faceValue
            ? (data.listing.unitCst?.amt ?? data.listing.faceValue?.amt ?? 0) *
              data.listing.availQty
            : null;

        if (amt == null) return null;

        return {
          amt,
          currency: data.listing.currency,
          dec: data.listing.unitCst?.dec ?? data.listing.faceValue?.dec ?? 2,
          disp: '', // Not used by MoneyCell when we have amt
        };
      },
      size: COLUMN_DEFAULT_SIZE_LG,
      useUserPrecision: true,
      withFooter: true,
      className: (data) =>
        isListingGroupLeader(data?.listing) ? styles.bold : undefined,
      shouldRender: (data) =>
        Boolean(
          data?.listing && !data?.isCompListing && !data?.shouldHideNumberColumn
        ),
      customPrecision: ({ value, userDefinedPrecision, isServerSetting }) => {
        const shouldRoundToWhole = !isServerSetting && value.amt > 999;
        return shouldRoundToWhole ? FormatOption.Whole : userDefinedPrecision;
      },
      sortingFn: groupSortingWrapper(
        sortMoney,
        ListingTableColumnId.UnsoldCost,
        canSortGroup,
        canSortAnySubrows
      ),
    }),
    [ListingTableColumnId.SoldCost]: createMoneyColumn<ListingWithEvent>({
      id: ListingTableColumnId.SoldCost,
      headerContentId:
        LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[ListingTableColumnId.SoldCost],
      accessor: (data) => {
        if (!data?.listing) return null;

        // Return server-side aggregated value when possible
        if (data.listing.isLtGrp && data.listing.soldCost) {
          return data.listing.soldCost;
        }

        const amt =
          data.listing.unitCst || data.listing.faceValue
            ? (data.listing.unitCst?.amt ?? data.listing.faceValue?.amt ?? 0) *
              (data.listing.ticketCnt - data.listing.availQty)
            : null;

        if (amt == null) return null;

        return {
          amt,
          currency: data.listing.currency,
          dec: data.listing.unitCst?.dec ?? data.listing.faceValue?.dec ?? 2,
          disp: '', // Not used by MoneyCell when we have amt
        };
      },
      size: COLUMN_DEFAULT_SIZE_LG,
      useUserPrecision: true,
      withFooter: true,
      className: (data) =>
        isListingGroupLeader(data?.listing) ? styles.bold : undefined,
      shouldRender: (data) =>
        Boolean(
          data?.listing && !data?.isCompListing && !data?.shouldHideNumberColumn
        ),
      customPrecision: ({ value, userDefinedPrecision, isServerSetting }) => {
        const shouldRoundToWhole = !isServerSetting && value.amt > 999;
        return shouldRoundToWhole ? FormatOption.Whole : userDefinedPrecision;
      },
      sortingFn: groupSortingWrapper(
        sortMoney,
        ListingTableColumnId.SoldCost,
        canSortGroup,
        canSortAnySubrows
      ),
    }),
    [ListingTableColumnId.TotalCost]: createMoneyColumn<ListingWithEvent>({
      id: ListingTableColumnId.TotalCost,
      headerContentId:
        LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[ListingTableColumnId.TotalCost],
      accessor: (data) => {
        if (!data?.listing) return null;

        const amt =
          data.listing.unitCst || data.listing.faceValue
            ? (data.listing.unitCst?.amt ?? data.listing.faceValue?.amt ?? 0) *
              data.listing.ticketCnt
            : null;

        if (amt == null) return null;

        return {
          amt,
          currency: data.listing.currency,
          dec: data.listing.unitCst?.dec ?? data.listing.faceValue?.dec ?? 2,
          disp: '', // Not used by MoneyCell when we have amt
        };
      },
      size: COLUMN_DEFAULT_SIZE_LG,
      useUserPrecision: true,
      withFooter: true,
      className: (data) =>
        isListingGroupLeader(data?.listing) ? styles.bold : undefined,
      shouldRender: (data) =>
        Boolean(
          data?.listing && !data?.isCompListing && !data?.shouldHideNumberColumn
        ),
      customPrecision: ({ value, userDefinedPrecision, isServerSetting }) => {
        const shouldRoundToWhole = !isServerSetting && value.amt > 999;
        return shouldRoundToWhole ? FormatOption.Whole : userDefinedPrecision;
      },
      sortingFn: groupSortingWrapper(
        sortMoney,
        ListingTableColumnId.TotalCost,
        canSortGroup,
        canSortAnySubrows
      ),
    }),
    [ListingTableColumnId.ListPrice]: columnHelper.accessor(
      (data) => data?.listing?.listPrice,
      {
        id: ListingTableColumnId.ListPrice,
        header: ({ column }) => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.ListPrice
              ]
            }
          />
        ),
        enableColumnFilter: true,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: ({ row: { original }, table }) => {
          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }

          if (!original.listing) return null;

          // We do not display pricing input for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return null;

          const sellerFee = original.listing.mkpListings?.find(
            (m) => m.mkp === Marketplace.StubHub
          )?.sellerFee;

          const listings: ListingWithEvent[] = table
            .getSortedRowModel()
            .rows.filter((row) => row?.original != null)
            .map((row) => row.original!);

          const nextEditableListing = getNextEditableListingRow(
            listings,
            original
          );

          return (
            <ListingPriceCell
              currencyCode={original.listing.currency}
              sellerFee={sellerFee}
              field="listPrice"
              otherField="allInPrice"
              listing={original.listing}
              viagVirtId={original.event?.virtId}
              disabled={!original.listing?.availQty}
              nextEditableListingId={nextEditableListing?.listing?.id}
              isV2Variant={true}
              parentStyles={styles.inputCell}
            />
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.ListPrice,
          canSortGroup,
          canSortAnySubrows
        ),
        filterFn: moneyRangeFilter,
        meta: {
          isEditable: true,
          columnFilterVariant: 'moneyRange',
        },
      }
    ),
    [ListingTableColumnId.AllInPrice]: columnHelper.accessor(
      (data) =>
        data?.isCompListing
          ? data.listing?.allInPrice
          : // listing all-in-price may not always have a value, so calling this function will properly handle calculating it from list-price if it's null
            getAllInPriceFromListPriceForListing(data?.listing),
      {
        id: ListingTableColumnId.AllInPrice,
        header: ({ column }) => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.AllInPrice
              ]
            }
          />
        ),
        enableColumnFilter: true,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: ({ row: { original }, table }) => {
          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }

          if (!original.listing) return null;

          if (original.isCompListing) {
            const value = original.listing.allInPrice;
            const currencyCode = original.listing.currency;

            return (
              <BaseCell align="right">
                <div>
                  {value != null &&
                    currencyCode != null &&
                    applyNumberFormatting({
                      inputNumber: value,
                      currencyCode: currencyCode,
                    })}
                </div>
              </BaseCell>
            );
          }

          // We do not display pricing input for listing group or comp listing
          if (original.listing.isLtGrp || !original.listing.isFull) return null;

          const sellerFee = original.listing.mkpListings?.find(
            (m) => m.mkp === Marketplace.StubHub
          )?.sellerFee;

          const listings: ListingWithEvent[] = table
            .getSortedRowModel()
            .rows.filter((row) => row?.original != null)
            .map((row) => row.original!);

          const nextEditableListing = getNextEditableListingRow(
            listings,
            original
          );

          const getAutoPriceResultStateBadge = (
            constraintType?: AutoPricingConstraintType | undefined
          ) => {
            if (!constraintType) {
              return null;
            }

            return (
              <AutoPriceResultStateBadge
                listingId={original.listing?.id}
                autoPricingResultType={
                  original.listing?.autoPrcResult ?? undefined
                }
                autoPricingResultConstraintType={constraintType}
                shouldShowResultTypeAction={
                  original.listing?.isAutoPrc ?? false
                }
              />
            );
          };

          const autoPriceResultStateBadge = getAutoPriceResultStateBadge(
            original.listing?.autoPrcConstraintType ?? undefined
          );

          const getAutoPriceResultStateBackgroundStyle = (
            constraintType?: AutoPricingConstraintType | undefined
          ) => {
            if (!constraintType) {
              return undefined;
            }

            switch (constraintType) {
              case AutoPricingConstraintType.HardConstraint:
                return styles.hardConstraintResultInputFieldStyles;
              case AutoPricingConstraintType.SoftConstraint:
                return styles.softConstraintResultInputFieldStyles;
              default:
                return undefined;
            }
          };

          const shouldShowAutoPriceResultStateBadge =
            original.listing?.isAutoPrc;

          const autoPriceResultStateCellStyle =
            shouldShowAutoPriceResultStateBadge
              ? getAutoPriceResultStateBackgroundStyle(
                  original.listing?.autoPrcConstraintType ?? undefined
                )
              : undefined;

          return (
            <ListingPriceCell
              currencyCode={original.listing.currency}
              sellerFee={sellerFee}
              field="allInPrice"
              otherField="listPrice"
              listing={original.listing}
              viagVirtId={original.event?.virtId}
              disabled={!original.listing?.availQty}
              nextEditableListingId={nextEditableListing?.listing?.id}
              isV2Variant={true}
              prefixDisplay={
                shouldShowAutoPriceResultStateBadge
                  ? autoPriceResultStateBadge
                  : null
              }
              parentStyles={clsx(
                styles.inputCell,
                autoPriceResultStateCellStyle
              )}
            />
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.AllInPrice,
          canSortGroup,
          canSortAnySubrows
        ),
        filterFn: moneyRangeFilter,
        meta: {
          isEditable: true,
          columnFilterVariant: 'moneyRange',
        },
      }
    ),
    [ListingTableColumnId.AutoPricing]: columnHelper.accessor(
      (data) => data?.listing?.isAutoPrc,
      {
        id: ListingTableColumnId.AutoPricing,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.AutoPricing
              ]
            }
          />
        ),
        enableColumnFilter: true,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ row: { original }, column }) {
          const editable = column.columnDef.meta?.isEditable ?? false;

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }

          if (!original.listing) return null;

          // We do not display pricing for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return null;

          if (!editable) {
            return <BaseCell />;
          }

          return (
            <BaseCell>
              <AutoPricingToggleCell
                listing={original.listing}
                event={original.event}
                editable={editable}
                isV2Variant={true}
              />
            </BaseCell>
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.AutoPricing,
          canSortGroup,
          canSortAnySubrows
        ),
        filterFn: booleanFilter,
        meta: {
          isEditable: editableColumns.has(ListingTableColumnId.AutoPricing),
          columnFilterVariant: 'boolean',
        },
      }
    ),
    [ListingTableColumnId.ProceedsFloor]: columnHelper.accessor(
      (data) => data?.listing?.procsFloor ?? 0,
      {
        id: ListingTableColumnId.ProceedsFloor,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.ProceedsFloor
              ]
            }
          />
        ),
        enableColumnFilter: true,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ row: { original }, getValue }) {
          const value = getValue<number>();

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;
          // We do not display pricing for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return null;

          const sellerFee = original.listing.mkpListings?.find(
            (m) => m.mkp === Marketplace.StubHub
          )?.sellerFee;

          return (
            <ListingProceedsFloorCell
              currencyCode={original.listing.currency}
              sellerFee={sellerFee}
              field="netProceedsFloor"
              listing={original.listing}
              viagVirtId={original.event?.virtId}
              disabled={!original.listing?.availQty}
              parentStyles={styles.inputCell}
              isV2Variant={true}
            />
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.ProceedsFloor,
          canSortGroup,
          canSortAnySubrows
        ),
        filterFn: moneyRangeFilter,
        meta: {
          isEditable: true,
          columnFilterVariant: 'moneyRange',
        },
      }
    ),
    [ListingTableColumnId.ProceedsCeiling]: createMoneyColumn<ListingWithEvent>(
      {
        id: ListingTableColumnId.ProceedsCeiling,
        headerContentId:
          LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
            ListingTableColumnId.ProceedsCeiling
          ],
        accessor: (data) => {
          if (!data?.listing) return null;
          const procsCeiling = data.listing.procsCeil;

          if (procsCeiling == null) return null;

          return {
            amt: procsCeiling,
            currency: data.listing.currency,
            dec: data.listing.currency ? 2 : 0,
            disp: null,
          };
        },
        size: COLUMN_DEFAULT_SIZE_LG,
        useUserPrecision: true,
        shouldRender: (data) =>
          !!(
            data?.listing &&
            !data.listing.isLtGrp &&
            data.listing.isFull &&
            !data.isCompListing
          ),
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.ProceedsCeiling,
          canSortGroup,
          canSortAnySubrows
        ),
      }
    ),
    [ListingTableColumnId.WebsiteFloor]: columnHelper.accessor(
      (data) => data?.listing?.procsFloor,
      {
        id: ListingTableColumnId.WebsiteFloor,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.WebsiteFloor
              ]
            }
          />
        ),
        enableColumnFilter: true,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ row: { original }, getValue }) {
          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;
          // We do not display pricing for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return null;

          const sellerFee = original.listing.mkpListings?.find(
            (m) => m.mkp === Marketplace.StubHub
          )?.sellerFee;

          return (
            <ListingProceedsFloorCell
              currencyCode={original.listing.currency}
              sellerFee={sellerFee}
              field="netProceedsFloor"
              isAllInPrice
              listing={original.listing}
              viagVirtId={original.event?.virtId}
              disabled={!original.listing?.availQty}
              parentStyles={styles.inputCell}
              isV2Variant={true}
            />
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.WebsiteFloor,
          canSortGroup,
          canSortAnySubrows
        ),
        filterFn: moneyRangeFilter,
        meta: {
          isEditable: true,
          columnFilterVariant: 'moneyRange',
        },
      }
    ),
    [ListingTableColumnId.WebsiteCeiling]: createMoneyColumn<ListingWithEvent>({
      id: ListingTableColumnId.WebsiteCeiling,
      headerContentId:
        LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
          ListingTableColumnId.WebsiteCeiling
        ],
      accessor: (data) => {
        if (!data?.listing) return null;
        const procsCeiling = data.listing.procsCeil;

        if (procsCeiling == null) return null;

        // Convert to all-in price
        const allInCeilingPrice = getAllInPriceFromListPrice(
          procsCeiling,
          data.listing
        );

        if (allInCeilingPrice == null) return null;

        return {
          amt: allInCeilingPrice,
          currency: data.listing.currency,
          dec: data.listing.currency ? 2 : 0,
          disp: null,
        };
      },
      size: COLUMN_DEFAULT_SIZE_LG,
      useUserPrecision: true,
      shouldRender: (data) =>
        !!(
          data?.listing &&
          !data.listing.isLtGrp &&
          data.listing.isFull &&
          !data.isCompListing
        ),
      sortingFn: groupSortingWrapper(
        sortingFns.basic,
        ListingTableColumnId.WebsiteCeiling,
        canSortGroup,
        canSortAnySubrows
      ),
    }),
    [ListingTableColumnId.UndercutMode]: columnHelper.accessor(
      (data) => data?.listing?.undMode,
      {
        id: ListingTableColumnId.UndercutMode,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.UndercutMode
              ]
            }
          />
        ),
        enableColumnFilter: false,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ row: { original }, column, getValue, table }) {
          const editable = column.columnDef.meta?.isEditable ?? false;
          const { userDefinedPrecision } = useColumnUserSetting(
            column.id,
            table.options.meta?.sectionType ?? SECTION_TYPE
          );
          const undercutMode = getValue<AutoPricingUndercutMode | undefined>();

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }

          if (!original.listing) return null;

          // We do not display pricing for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return null;

          const UndercutModeIcon = undercutMode
            ? getUndercutModeIcon(undercutMode)
            : null;
          return (
            <PricingModeCell
              pricingMode={undercutMode}
              DisplayIcon={
                userDefinedPrecision === FormatOption.EnumDisplay_Icon
                  ? UndercutModeIcon
                  : undefined
              }
              listing={original.listing}
              event={original.event}
              field={'undercutMode'}
              editable={editable}
            />
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.UndercutMode,
          canSortGroup,
          canSortAnySubrows
        ),
        meta: {
          isEditable: editableColumns.has(ListingTableColumnId.UndercutMode),
        },
      }
    ),
    [ListingTableColumnId.UndercutAbsoluteAmount]: columnHelper.accessor(
      (data) => data?.listing?.undAbsAmt,
      {
        id: ListingTableColumnId.UndercutAbsoluteAmount,
        header: function Cell() {
          const { getUiCurrency } = useLocalizationContext();
          const { loginContext } = useAppContext();
          const titleText = useContent(ContentId.UndercutAbsoluteAmount);

          const currencyCode = loginContext?.user?.activeAccount.currencyCode;
          const { sym } = getUiCurrency(currencyCode);

          return (
            <>
              <Content id={ContentId.UndercutAmount} />
              {` (${sym})`}
            </>
          );
        },
        enableColumnFilter: true,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ row: { original }, column, getValue, table }) {
          const value = getValue<number | null>();
          const { getUiCurrency } = useLocalizationContext();
          const currency = getUiCurrency(original?.listing?.currency);

          const editable = column.columnDef.meta?.isEditable ?? false;
          const { userDefinedPrecision } = useColumnUserSetting(
            column.id,
            table.options.meta?.sectionType ?? SECTION_TYPE
          );

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (
            !original.listing ||
            !original.listing?.isAutoPrc ||
            original.listing?.undMode == null
          )
            return <BaseCell />;

          const isPremium = value != null && value < 0;
          const formattedNumber =
            value != null
              ? applyNumberFormatting({
                  inputNumber: Math.abs(value),
                  precision: userDefinedPrecision,
                  currencyCode: currency.code,
                  currencyDecimalDigits: currency.dec,
                })
              : null;

          return (
            <BaseCell>
              <UndercutAmountCell
                formattedNumber={formattedNumber as string | null | undefined}
                isPremium={isPremium}
                listing={original.listing}
                viagVirtId={original.event?.virtId}
                field={'undercutAbsoluteAmount'}
                editable={editable}
              />
            </BaseCell>
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.UndercutAbsoluteAmount,
          canSortGroup,
          canSortAnySubrows
        ),
        filterFn: moneyRangeFilter,
        meta: {
          columnFilterVariant: 'moneyRange',
          isEditable: editableColumns.has(
            ListingTableColumnId.UndercutAbsoluteAmount
          ),
        },
      }
    ),
    [ListingTableColumnId.UndercutRelativeAmount]: columnHelper.accessor(
      (data) => data?.listing?.undRelAmt,
      {
        id: ListingTableColumnId.UndercutRelativeAmount,
        header: function Cell() {
          const titleText = useContent(ContentId.UndercutRelativeAmount);

          return (
            <>
              <Content id={ContentId.UndercutAmount} />
              {` (%)`}
            </>
          );
        },
        enableColumnFilter: true,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ row: { original }, column, getValue, table }) {
          const value = getValue<number | null>();

          const editable = column.columnDef.meta?.isEditable ?? false;
          const { userDefinedPrecision } = useColumnUserSetting(
            column.id,
            table.options.meta?.sectionType ?? SECTION_TYPE
          );

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }

          if (
            !original.listing ||
            !original.listing?.isAutoPrc ||
            original.listing?.undMode == null
          )
            return null;

          // We do not display pricing for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return null;

          const isPremium = value != null && value < 0;
          const formattedNumber =
            value != null
              ? applyNumberFormatting({
                  inputNumber: Math.abs(value),
                  precision: userDefinedPrecision,
                  isPercent: true,
                })
              : null;

          return (
            <BaseCell>
              <UndercutAmountCell
                formattedNumber={formattedNumber as string | null | undefined}
                isPremium={isPremium}
                listing={original.listing}
                viagVirtId={original.event?.virtId}
                field={'undercutRelativeAmount'}
                editable={editable}
              />
            </BaseCell>
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.UndercutRelativeAmount,
          canSortGroup,
          canSortAnySubrows
        ),
        filterFn: percentRangeFilter,
        meta: {
          columnFilterVariant: 'numberRange',
          isEditable: editableColumns.has(
            ListingTableColumnId.UndercutRelativeAmount
          ),
        },
      }
    ),
    [ListingTableColumnId.UndercutNewPrice]:
      createMoneyColumn<ListingWithEvent>({
        id: ListingTableColumnId.UndercutNewPrice,
        headerContentId:
          LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
            ListingTableColumnId.UndercutNewPrice
          ],
        accessor: (data) => {
          if (!data?.listing) return null;
          const listPrice = data.listing.undNewPrice;
          const allInPrice = getAllInPriceFromListPriceForListing(data.listing);

          if (allInPrice == null) return null;

          return {
            amt: allInPrice,
            currency: data.listing.currency,
            dec: data.listing.currency ? 2 : 0,
            disp: null,
          };
        },
        size: COLUMN_DEFAULT_SIZE_LG,
        useUserPrecision: true,
        shouldRender: (data) =>
          !!(
            data?.listing &&
            !data.listing.isLtGrp &&
            data.listing.isFull &&
            !data.isCompListing
          ),
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.UndercutNewPrice,
          canSortGroup,
          canSortAnySubrows
        ),
      }),
    [ListingTableColumnId.CompListingMode]: columnHelper.accessor(
      (data) => data?.listing?.compMode,
      {
        id: ListingTableColumnId.CompListingMode,
        header: () => <Content id={ContentId.CompMode} />,
        enableColumnFilter: true,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ row: { original }, column, getValue, table }) {
          const editable = column.columnDef.meta?.isEditable ?? false;
          const { userDefinedPrecision } = useColumnUserSetting(
            column.id,
            table.options.meta?.sectionType ?? SECTION_TYPE
          );
          const compListingMode = getValue<
            AutoPricingCompListingMode | undefined
          >();

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }

          if (!original.listing) return null;

          // We do not display pricing for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return <BaseCell />;

          const CompListingModeIcon = compListingMode
            ? getCompListingModeIcon(compListingMode)
            : null;

          return (
            <BaseCell>
              <PricingModeCell
                pricingMode={compListingMode}
                DisplayIcon={
                  userDefinedPrecision === FormatOption.EnumDisplay_Icon
                    ? CompListingModeIcon
                    : undefined
                }
                listing={original.listing}
                event={original.event}
                field={'compListingMode'}
                editable={editable}
              />
            </BaseCell>
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.CompListingMode,
          canSortGroup,
          canSortAnySubrows
        ),
        filterFn: multiSelectFilter,
        meta: {
          isEditable: editableColumns.has(ListingTableColumnId.CompListingMode),
          columnFilterVariant: 'multiSelect',
          uniqueValueVariant: 'compListingMode',
        },
      }
    ),
    [ListingTableColumnId.CompListingFloor]: columnHelper.accessor(
      (data) => data?.listing?.compFloor,
      {
        id: ListingTableColumnId.CompListingFloor,
        header: () => <Content id={ContentId.CompFloor} />,
        enableColumnFilter: false,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ row: { original }, column, getValue, table }) {
          const value = getValue<number | null>();

          const editable = column.columnDef.meta?.isEditable ?? false;
          const { userDefinedPrecision } = useColumnUserSetting(
            column.id,
            table.options.meta?.sectionType ?? SECTION_TYPE
          );

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;

          // We do not display comp listing floor if is listing group or comp listing modes not supporting it
          if (
            original.listing.isLtGrp ||
            original.isCompListing ||
            !original.listing.isFull ||
            (original.listing.compMode !==
              AutoPricingCompListingMode.QualityScore &&
              original.listing.compMode !==
                AutoPricingCompListingMode.SameSection)
          )
            return null;

          const formattedNumber = applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: value,
          });

          return (
            <CompListingBoundsCell
              formattedNumber={formattedNumber as string | null | undefined}
              listing={original.listing}
              field="compListingFloor"
              viagVirtId={original.event?.virtId}
              editable={editable}
            />
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.CompListingFloor,
          canSortGroup,
          canSortAnySubrows
        ),
        meta: {
          isEditable: editableColumns.has(
            ListingTableColumnId.CompListingFloor
          ),
        },
      }
    ),
    [ListingTableColumnId.CompListingCeiling]: columnHelper.accessor(
      (data) => data?.listing?.compCeil,
      {
        id: ListingTableColumnId.CompListingCeiling,
        header: () => <Content id={ContentId.CompCeiling} />,
        enableColumnFilter: false,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ row: { original }, column, getValue, table }) {
          const value = getValue<number | null>();

          const editable = column.columnDef.meta?.isEditable ?? false;
          const { userDefinedPrecision } = useColumnUserSetting(
            column.id,
            table.options.meta?.sectionType ?? SECTION_TYPE
          );

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;

          // We do not display pricing for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            original.isCompListing ||
            !original.listing.isFull ||
            (original.listing.compMode !==
              AutoPricingCompListingMode.QualityScore &&
              original.listing.compMode !==
                AutoPricingCompListingMode.SameSection)
          )
            return null;

          const formattedNumber = applyNumberFormatting({
            precision: userDefinedPrecision,
            inputNumber: value,
          });

          return (
            <CompListingBoundsCell
              formattedNumber={formattedNumber as string | null | undefined}
              listing={original.listing}
              field="compListingCeiling"
              viagVirtId={original.event?.virtId}
              editable={editable}
            />
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.CompListingCeiling,
          canSortGroup,
          canSortAnySubrows
        ),
        meta: {
          isEditable: editableColumns.has(
            ListingTableColumnId.CompListingCeiling
          ),
        },
      }
    ),
    [ListingTableColumnId.CompListingMinimumQuantity]: columnHelper.accessor(
      (data) => data?.listing?.compQtyScrAdjJson ?? '',
      {
        id: ListingTableColumnId.CompListingMinimumQuantity,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.CompListingMinimumQuantity
              ]
            }
          />
        ),
        enableColumnFilter: false,
        filterFn: compListingMinQuantityFilter,
        meta: {
          columnFilterVariant: 'numberRange',
          isEditable: editableColumns.has(
            ListingTableColumnId.CompListingMinimumQuantity
          ),
        },
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ row: { original }, column }) {
          const editable = column.columnDef.meta?.isEditable ?? false;

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;
          // We do not display pricing for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return null;

          return (
            <BaseCell showPadding={false} isFullWidth>
              <CompListingMinQuantityCell
                listing={original.listing}
                editable={editable}
                event={original.event}
                isV2Variant={true}
              />
            </BaseCell>
          );
        },
        sortingFn: groupSortingWrapper(
          sortGeneric,
          ListingTableColumnId.CompListingMinimumQuantity,
          canSortGroup,
          canSortAnySubrows
        ),
      }
    ),
    [ListingTableColumnId.CompListingExcludeFanInventory]:
      columnHelper.accessor((data) => data?.listing?.compExclFanInv ?? false, {
        id: ListingTableColumnId.CompListingExcludeFanInventory,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.CompListingExcludeFanInventory
              ]
            }
          />
        ),
        enableColumnFilter: true,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ row: { original }, column }) {
          const editable = column.columnDef.meta?.isEditable ?? false;

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;
          // We do not display pricing for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return null;

          return (
            <BaseCell>
              <CompListingExcludeFanInventoryCell
                listing={original.listing}
                editable={editable}
                event={original.event}
                isV2Variant={true}
              />
            </BaseCell>
          );
        },
        sortingFn: groupSortingWrapper(
          sortGeneric,
          ListingTableColumnId.CompListingExcludeFanInventory,
          canSortGroup,
          canSortAnySubrows
        ),
        filterFn: booleanFilter,
        meta: {
          isEditable: editableColumns.has(
            ListingTableColumnId.CompListingExcludeFanInventory
          ),
          columnFilterVariant: 'boolean',
        },
      }),
    [ListingTableColumnId.OutlierMode]: columnHelper.accessor(
      (data) => data?.listing?.outMode ?? '',
      {
        id: ListingTableColumnId.OutlierMode,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.OutlierMode
              ]
            }
          />
        ),
        enableColumnFilter: true,
        filterFn: multiSelectFilter,
        meta: {
          columnFilterVariant: 'multiSelect',
          uniqueValueVariant: 'outlierMode',
          isEditable: editableColumns.has(ListingTableColumnId.OutlierMode),
        },
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ row: { original }, column }) {
          const editable = column.columnDef.meta?.isEditable ?? false;

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;
          // We do not display pricing for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return null;

          return (
            <BaseCell showPadding={false} isFullWidth>
              <OutlierModeCell
                listing={original.listing}
                editable={editable}
                event={original.event}
                isV2Variant={true}
              />
            </BaseCell>
          );
        },
        sortingFn: groupSortingWrapper(
          sortGeneric,
          ListingTableColumnId.OutlierMode,
          canSortGroup,
          canSortAnySubrows
        ),
      }
    ),
    [ListingTableColumnId.OutlierFiltering]: columnHelper.accessor(
      (data) => data?.listing?.outlStdDev ?? '',
      {
        id: ListingTableColumnId.OutlierFiltering,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.OutlierFiltering
              ]
            }
          />
        ),
        enableColumnFilter: true,
        filterFn: outlierStdDevFilter,
        meta: {
          columnFilterVariant: 'multiSelect',
          uniqueValueVariant: 'outlierStdDev',
          isEditable: editableColumns.has(
            ListingTableColumnId.OutlierFiltering
          ),
        },
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        sortingFn: groupSortingWrapper(
          sortGeneric,
          ListingTableColumnId.OutlierFiltering,
          canSortGroup,
          canSortAnySubrows
        ),
        cell: function Cell({ row: { original }, column }) {
          const editable = column.columnDef.meta?.isEditable ?? false;

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;
          // We do not display pricing for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return null;

          return (
            <BaseCell showPadding={false} isFullWidth>
              <OutlierFilteringCell
                listing={original.listing}
                editable={editable}
                event={original.event}
                isV2Variant={true}
              />
            </BaseCell>
          );
        },
      }
    ),
    [ListingTableColumnId.OutlierLimit]: columnHelper.accessor(
      (data) => data?.listing?.outKthLowestLim ?? '',
      {
        id: ListingTableColumnId.OutlierLimit,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.OutlierLimit
              ]
            }
          />
        ),
        enableColumnFilter: true,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        sortingFn: groupSortingWrapper(
          sortGeneric,
          ListingTableColumnId.OutlierLimit,
          canSortGroup,
          canSortAnySubrows
        ),
        cell: function Cell({ row: { original }, column }) {
          const editable = column.columnDef.meta?.isEditable ?? false;

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;
          // We do not display pricing for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return null;

          if (!editable) {
            return (
              <TextCell
                align="right"
                value={original.listing.outKthLowestLim}
              />
            );
          }

          return (
            <OutlierLimitCell
              listing={original.listing}
              editable={editable}
              event={original.event}
              isV2Variant={true}
            />
          );
        },
        filterFn: numberRangeFilter,
        meta: {
          isEditable: editableColumns.has(ListingTableColumnId.OutlierLimit),
          columnFilterVariant: 'numberRange',
        },
      }
    ),
    [ListingTableColumnId.OutlierSpacing]: columnHelper.accessor(
      (data) => data?.listing?.outKthLowestLimAbsSpc ?? '',
      {
        id: ListingTableColumnId.OutlierSpacing,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.OutlierSpacing
              ]
            }
          />
        ),
        enableColumnFilter: true,
        filterFn: outlierSpacingFilter,
        meta: {
          columnFilterVariant: 'dualModeNumber',
          isEditable: editableColumns.has(ListingTableColumnId.OutlierSpacing),
        },
        size: COLUMN_DEFAULT_SIZE_XL,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        sortingFn: groupSortingWrapper(
          sortGeneric,
          ListingTableColumnId.OutlierSpacing,
          canSortGroup,
          canSortAnySubrows
        ),
        cell: function Cell({ row: { original }, column }) {
          const editable = column.columnDef.meta?.isEditable ?? false;

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;
          // We do not display pricing for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return null;

          return (
            <OutlierSpacingCell
              listing={original.listing}
              editable={editable}
              event={original.event}
              isV2Variant={true}
            />
          );
        },
      }
    ),
    [ListingTableColumnId.CircuitBreakerMinCompListingCount]:
      columnHelper.accessor((data) => data?.listing?.cirBrMinCompCount ?? '', {
        id: ListingTableColumnId.CircuitBreakerMinCompListingCount,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.CircuitBreakerMinCompListingCount
              ]
            }
          />
        ),
        enableColumnFilter: false,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        sortingFn: groupSortingWrapper(
          sortGeneric,
          ListingTableColumnId.CircuitBreakerMinCompListingCount,
          canSortGroup,
          canSortAnySubrows
        ),
        cell: function Cell({ row: { original }, column }) {
          const editable = column.columnDef.meta?.isEditable ?? false;

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;
          // We do not display pricing for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return null;

          return (
            <CircuitBreakerMinCompListingCountCell
              listing={original.listing}
              editable={editable}
              event={original.event}
            />
          );
        },
        meta: {
          isEditable: editableColumns.has(
            ListingTableColumnId.CircuitBreakerMinCompListingCount
          ),
        },
      }),
    [ListingTableColumnId.CircuitBreakerVelocity]: columnHelper.accessor(
      (data) => data?.listing?.cirBrMinCompCount ?? '',
      {
        id: ListingTableColumnId.CircuitBreakerVelocity,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.CircuitBreakerVelocity
              ]
            }
          />
        ),
        enableColumnFilter: false,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        sortingFn: groupSortingWrapper(
          sortGeneric,
          ListingTableColumnId.CircuitBreakerVelocity,
          canSortGroup,
          canSortAnySubrows
        ),
        cell: function Cell({ row: { original }, column }) {
          const editable = column.columnDef.meta?.isEditable ?? false;

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;
          // We do not display pricing for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return null;

          return (
            <CircuitBreakerVelocityCell
              listing={original.listing}
              editable={editable}
              event={original.event}
            />
          );
        },
        meta: {
          isEditable: editableColumns.has(
            ListingTableColumnId.CircuitBreakerVelocity
          ),
        },
      }
    ),
    [ListingTableColumnId.CircuitBreakerVelocityHours]: columnHelper.accessor(
      (data) => data?.listing?.cirBrMinCompCount ?? '',
      {
        id: ListingTableColumnId.CircuitBreakerVelocityHours,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.CircuitBreakerVelocityHours
              ]
            }
          />
        ),
        enableColumnFilter: false,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        sortingFn: groupSortingWrapper(
          sortGeneric,
          ListingTableColumnId.CircuitBreakerVelocityHours,
          canSortGroup,
          canSortAnySubrows
        ),
        cell: function Cell({ row: { original }, column }) {
          const editable = column.columnDef.meta?.isEditable ?? false;

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;
          // We do not display pricing for listing group or comp listing
          if (
            original.listing.isLtGrp ||
            !original.listing.isFull ||
            original.isCompListing
          )
            return null;

          return (
            <CircuitBreakerVelocityHoursCell
              listing={original.listing}
              editable={editable}
              event={original.event}
            />
          );
        },
        meta: {
          isEditable: editableColumns.has(
            ListingTableColumnId.CircuitBreakerVelocityHours
          ),
        },
      }
    ),
    [ListingTableColumnId.SeatingRow]: columnHelper.accessor(
      (data) => data?.listing?.seating?.row ?? '',
      {
        id: ListingTableColumnId.SeatingRow,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.SeatingRow
              ]
            }
          />
        ),
        enableColumnFilter: true,
        meta: {
          columnFilterVariant: 'multiSelect',
        },
        filterFn: multiSelectFilter,
        size: COLUMN_DEFAULT_SIZE_BASE,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        sortingFn: groupSortingWrapper(
          sortGeneric,
          ListingTableColumnId.SeatingRow,
          canSortGroup,
          canSortAnySubrows
        ),
        cell: ({ row: { original }, getValue }) => {
          const value = getValue<string>();

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;
          // We do not display seating info for listing group
          if (original.listing.isLtGrp) return null;

          return <BaseCell align="right">{value}</BaseCell>;
        },
      }
    ),
    [ListingTableColumnId.SeatingSeats]: columnHelper.accessor(
      (data) => data?.listing?.seating,
      {
        id: ListingTableColumnId.SeatingSeats,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.SeatingSeats
              ]
            }
          />
        ),
        enableColumnFilter: true,
        filterFn: seatRangeFilter as FilterFnOption<ListingWithEvent | null>,
        meta: {
          columnFilterVariant: 'numberRange',
        },
        size: COLUMN_DEFAULT_SIZE_BASE,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        sortingFn: groupSortingWrapper(
          sortSeatingSeatsForTable,
          ListingTableColumnId.SeatingSeats,
          canSortGroup,
          canSortAnySubrows
        ),
        cell: function Cell({
          row: { original },
          column: { id: columnId },
          table,
        }) {
          const { userDefinedPrecision } = useColumnUserSetting(
            columnId,
            table.options.meta?.sectionType ?? SECTION_TYPE
          );
          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;
          // We do not display seating info for listing group
          if (original.listing.isLtGrp) return null;

          const { orgSeatFr, orgSeatTo, seating, availQty } =
            original?.listing ?? {};
          const { seatFr, seatTo } = seating ?? {};

          return (
            <BaseCell align="right">
              <SeatingInfo
                section={''}
                row={null}
                fontSize={vars.typography.fontSize.base}
                seatFr={seatFr}
                seatTo={seatTo}
                orgSeatFr={orgSeatFr}
                orgSeatTo={orgSeatTo}
                showOrgSeat={
                  userDefinedPrecision === FormatOption.Seating_OriginalSeating
                }
                showSeatPrefix={false}
                isSoldOut={availQty === 0}
                justifyIconContent="spaceBetween"
              />
            </BaseCell>
          );
        },
      }
    ),
    [ListingTableColumnId.AutoPricingResultType]: columnHelper.accessor(
      (data) => data?.listing?.autoPrcResult,
      {
        id: ListingTableColumnId.AutoPricingResultType,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.AutoPricingResultType
              ]
            }
          />
        ),
        enableColumnFilter: true,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        sortingFn: groupSortingWrapper(
          sortGeneric,
          ListingTableColumnId.AutoPricingResultType,
          canSortGroup,
          canSortAnySubrows
        ),
        cell: ({ row: { original }, getValue }) => {
          const value = getValue<AutoPricingResultType | null | undefined>();

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;
          // We do not display pricing result type info for listing group or comp listing
          if (original.listing.isLtGrp || original.isCompListing) return null;

          return (
            <BaseCell align="center" showTooltip={false}>
              <AutoPriceResultDisplay resultType={value} />
            </BaseCell>
          );
        },
        filterFn: multiSelectFilter,
        meta: {
          columnFilterVariant: 'multiSelect',
        },
      }
    ),
    [ListingTableColumnId.LastPriceUpdatedDate]:
      createDateColumn<ListingWithEvent | null>({
        id: ListingTableColumnId.LastPriceUpdatedDate,
        headerContentId:
          LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
            ListingTableColumnId.LastPriceUpdatedDate
          ],
        accessor: (data) => data?.listing?.lastPrcUpdOn ?? null,
        defaultPrecision: FormatOption.DateTime_MonthDD_Time,
        size: COLUMN_DEFAULT_SIZE_LG,
        enableUtcFallback: true,
        hideRelativeTerms: false,
        sectionType: SECTION_TYPE,
        sortingFn: groupSortingWrapper(
          sortingFns.datetime,
          ListingTableColumnId.LastPriceUpdatedDate,
          canSortGroup,
          canSortAnySubrows
        ),
        shouldRender: (data) =>
          !!(data?.listing && !data.listing.isLtGrp && !data.isCompListing),
        isLoading: (data) =>
          Boolean(!data) || Boolean(!data?.listing && data?.isLtGrp),
      }),
    [ListingTableColumnId.PurchaseCreatedBy]:
      createUserTagColumn<ListingWithEvent | null>({
        id: ListingTableColumnId.PurchaseCreatedBy,
        headerContentId:
          LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
            ListingTableColumnId.PurchaseCreatedBy
          ],
        accessor: (data) => data?.listing?.insertBy,
        size: COLUMN_DEFAULT_SIZE_LG,
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.PurchaseCreatedBy,
          canSortGroup,
          canSortAnySubrows
        ),
      }),
    [ListingTableColumnId.PurchasedBy]:
      createUserTagColumn<ListingWithEvent | null>({
        id: ListingTableColumnId.PurchasedBy,
        headerContentId:
          LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
            ListingTableColumnId.PurchasedBy
          ],
        accessor: (data) => {
          // Return array as string (will be parsed in cell component)
          const poBys = data?.listing?.poBys;
          return poBys
            ? Array.isArray(poBys)
              ? poBys.join(',')
              : poBys
            : null;
        },
        size: COLUMN_DEFAULT_SIZE_LG,
        useUserDisplayHook: useContextBasedUsers,
        shouldRender: (data) =>
          Boolean(
            data?.listing &&
            !data?.listing?.isLtGrp &&
            !data?.isCompListing &&
            data.listing?.dataIsLoaded?.[ListingDetailDataField.Purchase]
          ),
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.PurchasedBy,
          canSortGroup,
          canSortAnySubrows
        ),
      }),
    [ListingTableColumnId.PurchaseDate]:
      createDateColumn<ListingWithEvent | null>({
        id: ListingTableColumnId.PurchaseDate,
        headerContentId:
          LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
            ListingTableColumnId.PurchaseDate
          ],
        accessor: (data) => data?.listing?.poDate ?? null,
        defaultPrecision: FormatOption.DateTime_MonthDD_Time,
        size: COLUMN_DEFAULT_SIZE_LG,
        enableUtcFallback: true,
        hideRelativeTerms: true,
        sectionType: SECTION_TYPE,
        sortingFn: groupSortingWrapper(
          sortingFns.datetime,
          ListingTableColumnId.PurchaseDate,
          canSortGroup,
          canSortAnySubrows
        ),
        shouldRender: (data) => !!(data?.listing && !data.listing.isLtGrp),
        isLoading: (data) =>
          !data ||
          (!data.listing && data?.isLtGrp) ||
          (!data?.listing?.isLtGrp &&
            !data?.isCompListing &&
            !data.listing?.dataIsLoaded?.[ListingDetailDataField.Purchase]),
        getShimmerHeight: (data) =>
          data?.isLtGrp
            ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
            : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING,
      }),
    [ListingTableColumnId.WastedCost]: createMoneyColumn<ListingWithEvent>({
      id: ListingTableColumnId.WastedCost,
      headerContentId: ContentId.WastedCost,
      accessor: (data) => {
        if (!data || !data.listing) return null;

        const amt =
          (data.listing.wastedQty ?? 0) *
          (data.listing.unitCst?.amt || data.listing.faceValue?.amt || 0);

        if (amt == null) return null;

        return {
          amt,
          currency: data.listing.currency,
          dec: data.listing.unitCst?.dec ?? data.listing.faceValue?.dec ?? 2,
          disp: '', // Not used by MoneyCell when we have amt
        };
      },
      useUserPrecision: true,
      withFooter: true,
      shouldRender: (data) =>
        Boolean(
          data?.listing && !data?.listing?.isLtGrp && !data?.isCompListing
        ),
      customPrecision: ({ value, userDefinedPrecision, isServerSetting }) => {
        const shouldRoundToWhole = !isServerSetting && value.amt > 999;
        return shouldRoundToWhole ? FormatOption.Whole : userDefinedPrecision;
      },
      sortingFn: groupSortingWrapper(
        sortMoney,
        ListingTableColumnId.WastedCost,
        canSortGroup,
        canSortAnySubrows
      ),
    }),
    [ListingTableColumnId.WastedTickets]: createNumberColumn<ListingWithEvent>({
      id: ListingTableColumnId.WastedTickets,
      headerContentId: ContentId.WastedQty,
      accessor: (data) => data?.listing?.wastedQty ?? null,
      useUserPrecision: true,
      withFooter: true,
      shouldRender: (data) =>
        Boolean(
          data?.listing && !data?.listing?.isLtGrp && !data?.isCompListing
        ),
      sortingFn: groupSortingWrapper(
        sortingFns.basic,
        ListingTableColumnId.WastedTickets,
        canSortGroup,
        canSortAnySubrows
      ),
    }),
    [ListingTableColumnId.Actions]: columnHelper.accessor(
      // We don't actually use this, but this column is to be sorted by broad-casting state and quantity
      // And we need accessorFn to get the sorting activated
      (data) => data?.listing?.availQty,
      {
        id: ListingTableColumnId.Actions,
        enableSorting: false,
        enableResizing: true,
        header: function Cell() {
          return inFullPageEventView ? (
            <ColumnPicker tableColumnsSectionType={SectionType.Listings} />
          ) : null;
        },
        enableColumnFilter: false,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        sortingFn: groupSortingWrapper((r1, r2) => {
          if (r1.original?.listing?.isLtGrp) {
            if (!r2.original?.listing?.isLtGrp) {
              // listing groups sort first (sort r1 before r2)
              // unless this is a sub-level which then listing-group sort last
              return r2.original?.listing?.ltGrpId ===
                r1.original?.listing?.ltGrpId
                ? 1
                : -1;
            }
          }

          if (r2.original?.listing?.isLtGrp) {
            // listing groups sort first (sort r1 after r2)
            // unless this is a sub-level which then listing-group sort last
            return r2.original?.listing?.ltGrpId ===
              r1.original?.listing?.ltGrpId
              ? -1
              : 1;
          }

          const a = r1.original?.listing;
          const b = r2.original?.listing;
          const aQuantity = a?.availQty ?? 0;
          const bQuantity = b?.availQty ?? 0;
          if (aQuantity <= 0 && bQuantity >= 0) {
            return 1;
          } else if (aQuantity >= 0 && bQuantity <= 0) {
            return -1;
          }
          return (a?.ltGrpPrior ?? -1) - (b?.ltGrpPrior ?? -1);
        }),
        cell: ({
          row: {
            parentId,
            original,
            depth,
            getParentRows,
            getCanExpand,
            getIsExpanded,
            toggleExpanded,
          },
          table,
        }) => {
          const canExpand = getCanExpand();
          const isExpanded = getIsExpanded();
          const Icon = isExpanded ? ChevronUpIconV2 : ChevronDownIconV2;

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }

          if (original.listing?.isLtGrp && canExpand) {
            return (
              <>
                <div
                  style={{
                    marginRight: vars.spacing.xs,
                  }}
                >
                  <Icon
                    size={vars.iconSize.s}
                    withHoverEffect
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleExpanded(!isExpanded);
                    }}
                  />
                </div>
                {isListingGroupLeader(original.listing) && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleExpanded(!isExpanded);
                    }}
                  >
                    <ListingGroupIcon
                      stroke={vars.color.textBrand}
                      size={vars.iconSize.m}
                      withHoverEffect
                    />
                  </div>
                )}
              </>
            );
          }

          if (!original.listing || original.isCompListing) return null;

          const parentRows =
            depth > 0 && parentId && table.getRowModel().rowsById[parentId]
              ? getParentRows()
              : undefined;

          if (depth === 0 && original.listing.ltGrpId === FAKE_SOLD_GROUP_ID) {
            return null;
          }

          const linkOnly =
            table.options.meta?.formatOptionOverrides?.[
              ListingTableColumnId.Actions
            ] === FormatOption.Actions_LinkOnly;

          return (
            <BaseCell>
              <ListingActionsCellV2
                linkOnly={linkOnly}
                event={original.event}
                listing={original.listing}
                isSubRow={depth > 0}
                disabled={parentRows?.some(
                  (pr) =>
                    (pr.original?.listing as ListingGroup)?.isLockedFromEdits
                )}
              />
            </BaseCell>
          );
        },
      }
    ),
    [ListingTableColumnId.PurchaseId]:
      createLinkColumn<ListingWithEvent | null>({
        id: ListingTableColumnId.PurchaseId,
        headerContentId: ContentId.ExternalPurchaseIdAbbreviated,
        linkAccessor: (data) =>
          data?.listing?.poId
            ? getPurchaseOrderRelativeUrl(data.listing.poId)
            : null,
        textAccessor: (data) => data?.listing?.poOrderId ?? null,
        size: COLUMN_DEFAULT_SIZE_LG,
        textOnly: false,
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.PurchaseId,
          canSortGroup,
          canSortAnySubrows
        ),
        shouldRenderCell: (data) =>
          !!(
            data?.listing &&
            !data.isCompListing &&
            data.listing.poOrderId &&
            (data.listing.isLtGrp ||
              data.isCompListing ||
              data.listing.dataIsLoaded?.[ListingDetailDataField.Purchase])
          ),
      }),
    [ListingTableColumnId.VendorAccount]:
      createCopyColumn<ListingWithEvent | null>({
        id: ListingTableColumnId.VendorAccount,
        headerContentId: ContentId.VendorAccount,
        accessor: (data) => data?.listing?.poVendAccEmail?.toLowerCase() || '',
        size: COLUMN_DEFAULT_SIZE_LG,
        enableColumnFilter: true,
        shouldRender: (data) => {
          const listing = data?.listing;
          return Boolean(
            listing &&
            !data?.isCompListing &&
            (listing.isLtGrp ||
              listing.dataIsLoaded?.[ListingDetailDataField.Purchase])
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.VendorAccount,
          canSortGroup,
          canSortAnySubrows
        ),
      }),
    [ListingTableColumnId.AutoFulfill]: columnHelper.accessor(
      (data) => data?.listing?.isNoFulfill ?? '',
      {
        id: ListingTableColumnId.AutoFulfill,
        header: () => <Content id={ContentId.AutoFulfill} />,
        enableColumnFilter: false,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,

        cell: function Cell({ getValue, row: { original } }) {
          if (!original || !original.listing) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }

          // We do not display for comp listing or listing-group
          if (original.isLtGrp || original.isCompListing) return null;

          return (
            <BaseCell align="left">
              {/* AutoFulfill is negative of IsNoFulfill */}
              <Content id={getValue() ? ContentId.No : ContentId.Yes} />
            </BaseCell>
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.AutoFulfill,
          canSortGroup,
          canSortAnySubrows
        ),
      }
    ),
    [ListingTableColumnId.MarketplaceVisibility]: {
      ...createPercentColumn<ListingWithEvent | null>({
        id: ListingTableColumnId.MarketplaceVisibility,
        headerContentId: ContentId.MarketplaceVisibility,
        accessor: (data) => {
          const mkpVis = data?.listing?.mkpVis;
          // Convert from stored format to decimal (e.g., 50 -> 0.5 for 50%)
          return mkpVis != null ? Math.abs(Number(mkpVis) / 100) : null;
        },
        size: COLUMN_DEFAULT_SIZE_LG,
        sectionType: SECTION_TYPE,
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.MarketplaceVisibility,
          canSortGroup,
          canSortAnySubrows
        ),
        shouldRender: (data) => !data?.isCompListing,
        isLoading: (data) => !data || !data.listing,
        getShimmerHeight: (data) =>
          data?.isLtGrp
            ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
            : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING,
      }),
      header: function Cell() {
        const titleText = useContent(ContentId.MarketplaceVisibility);

        return (
          <>
            <Content id={ContentId.MarketplaceVisibility} />
            {` (%)`}
          </>
        );
      },
    },
    [ListingTableColumnId.AbsoluteRankPremium]:
      createMoneyColumn<ListingWithEvent>({
        id: ListingTableColumnId.AbsoluteRankPremium,
        headerContentId: ContentId.AbsoluteRankPremium,
        accessor: (data) => {
          const amount = data?.listing?.ltGrpUndercut?.actRankUndAbsAmt;
          const currency = data?.listing?.currency;

          if (amount == null) return null;
          return {
            amt: amount,
            currency: currency || null,
            disp: null,
            dec: 2,
          } as UiMoney;
        },
        enableColumnFilter: true,
        shouldRender: (data) => {
          const listing = data?.listing;
          return Boolean(
            listing &&
            !listing.isLtGrp &&
            listing.isFull &&
            listing.ltGrpUndercut?.actRankUndAbsAmt != null
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.AbsoluteRankPremium,
          canSortGroup,
          canSortAnySubrows
        ),
      }),
    [ListingTableColumnId.RelativeRankPremium]:
      createPercentColumn<ListingWithEvent>({
        id: ListingTableColumnId.RelativeRankPremium,
        headerContentId: ContentId.RelativeRankPremium,
        accessor: (data) => data?.listing?.ltGrpUndercut?.actRankUndRelAmt,
        size: COLUMN_DEFAULT_SIZE_LG,
        sectionType: SECTION_TYPE,
        shouldRender: (data) => {
          const listing = data?.listing;
          return Boolean(
            listing &&
            !listing.isLtGrp &&
            listing.isFull &&
            listing.ltGrpUndercut?.actRankUndRelAmt != null
          );
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.RelativeRankPremium,
          canSortGroup,
          canSortAnySubrows
        ) as any,
      }),

    [ListingTableColumnId.InternalNotes]: columnHelper.accessor(
      (data) => data?.listing?.internalNotesPreview,
      {
        id: ListingTableColumnId.InternalNotes,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.InternalNotes
              ]
            }
          />
        ),
        enableColumnFilter: true,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ getValue, row: { original } }) {
          const internalNotesPreview = getValue<string | null>();

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;

          // We do not display for comp listing
          if (original.isCompListing) return null;

          if (original.listing.isLtGrp || original.isCompListing) return null;

          return <BaseCell align="left">{internalNotesPreview}</BaseCell>;
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.InternalNotes,
          canSortGroup,
          canSortAnySubrows
        ),
        filterFn: textSearchFilter,
        meta: {
          columnFilterVariant: 'textSearch',
        },
      }
    ),
    [ListingTableColumnId.MarketplaceBroadcastStatus]: columnHelper.accessor(
      (data) => data?.listing?.availQty,
      {
        id: ListingTableColumnId.MarketplaceBroadcastStatus,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.MarketplaceBroadcastStatus
              ]
            }
          />
        ),
        enableColumnFilter: true,
        filterFn: marketplaceBroadcastFilter,
        meta: {
          columnFilterVariant: 'multiSelect',
          uniqueValueVariant: 'marketplaceBroadcast',
        },
        // editableColumn: true,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,

        cell: ({
          row: { parentId, original, depth, getParentRows },
          table,
        }) => {
          if (!original || !original.listing) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }

          // We do not display for comp listing or listing-group
          if (original.isLtGrp || original.isCompListing) return null;

          const parentRows =
            depth > 0 && parentId && table.getRowModel().rowsById[parentId]
              ? getParentRows()
              : undefined;

          return (
            <BaseCell align="left" showTooltip={false}>
              <MarketplaceBroadcastCell
                listingWithEvent={original}
                disabled={parentRows?.some(
                  (pr) =>
                    (pr.original?.listing as ListingGroup)?.isLockedFromEdits
                )}
              />
            </BaseCell>
          );
        },
        enableSorting: false,
      }
    ),
    [ListingTableColumnId.ConsigneeSellerAccountId]: columnHelper.accessor(
      (data) => data?.listing?.accId,
      {
        id: ListingTableColumnId.ConsigneeSellerAccountId,
        header: () => (
          <Content
            id={
              LISTING_TABLE_COLUMN_ID_TO_CONTENT_ID[
                ListingTableColumnId.ConsigneeSellerAccountId
              ]
            }
          />
        ),
        enableColumnFilter: false,
        size: COLUMN_DEFAULT_SIZE_LG,
        minSize: COLUMN_MIN_SIZE,
        maxSize: COLUMN_MAX_SIZE,
        cell: function Cell({ getValue, row: { original } }) {
          const { accountName } =
            useConsigneeAccountDisplayName(getValue<string | null>());

          if (!original || (!original.listing && original?.isLtGrp)) {
            return (
              <TableShimmeringDiv
                height={
                  original?.isLtGrp
                    ? SHIMMERING_DIV_HEIGHT_LISTING_GROUP
                    : SHIMMERING_DIV_HEIGHT_SINGLE_LISTING
                }
              />
            );
          }
          if (!original.listing) return null;
          if (original.listing.isLtGrp || original.isCompListing) return null;

          return <BaseCell align="left">{accountName}</BaseCell>;
        },
        sortingFn: groupSortingWrapper(
          sortingFns.basic,
          ListingTableColumnId.ConsigneeSellerAccountId,
          canSortGroup,
          canSortAnySubrows
        ),
      }
    ),
  } as const;
};
